import {Dialog} from '@common/ui/overlays/dialog/dialog'
import {DialogHeader} from '@common/ui/overlays/dialog/dialog-header'
import {DialogBody} from '@common/ui/overlays/dialog/dialog-body'
import {DialogFooter} from '@common/ui/overlays/dialog/dialog-footer'
import {Button} from '@common/ui/buttons/button'
import {useDialogContext} from '@common/ui/overlays/dialog/dialog-context'
import {Trans} from '@common/i18n/trans'
import {message} from '@common/i18n/message'
import {useState, useEffect, useRef} from 'react'
import {ContentCopyIcon} from '@common/icons/material/ContentCopy'
import {QrCodeIcon} from '@common/icons/material/QrCode'
import {LinkIcon} from '@common/icons/material/Link'
import {CheckCircleIcon} from '@common/icons/material/CheckCircle'
import {DownloadIcon} from '@common/icons/material/Download'
import {toast} from '@common/ui/toast/toast'
import useClipboard from 'react-use-clipboard'

interface QrCodeModalProps {
  shortUrl: string
  linkId?: number
}

export function QrCodeModal({shortUrl, linkId}: QrCodeModalProps) {
  const {close} = useDialogContext()
  const [qrSvg, setQrSvg] = useState<string>('')
  const [isLoading, setIsLoading] = useState(true)
  const [copiedRecently, setCopiedRecently] = useState(false)
  const [downloadFormat, setDownloadFormat] = useState<'svg' | 'png' | null>(null)
  const qrRef = useRef<HTMLDivElement>(null)
  const qrPageUrl = `${shortUrl}/qr`
  const [, copyToClipboard] = useClipboard(qrPageUrl, {
    successDuration: 100,
  })

  useEffect(() => {
    const loadQrCode = async () => {
      try {
        // Use linkId if provided, otherwise extract from shortUrl
        const id = linkId || shortUrl.split('/').pop();
        const response = await fetch(`/api/v1/link/${id}/qr`, {
          headers: {Accept: 'image/svg+xml'},
        })
        if (!response.ok) throw new Error(`HTTP ${response.status}`)

        let svg = await response.text()

        const originalWidthMatch = svg.match(/width=['"]?(\d+)['"]?/)
        const originalHeightMatch = svg.match(/height=['"]?(\d+)['"]?/)
        const originalWidth = originalWidthMatch ? originalWidthMatch[1] : '200'
        const originalHeight = originalHeightMatch ? originalHeightMatch[1] : '200'

        // جعل الـ SVG قابل للتمدد
        svg = svg.replace(/width="[^"]*"/g, '')
        svg = svg.replace(/height="[^"]*"/g, '')
        svg = svg.replace(
          '<svg',
          '<svg width="100%" height="100%" preserveAspectRatio="xMidYMid meet" style="width:100%;height:100%;display:block;"',
        )
        if (!svg.includes('viewBox')) {
          svg = svg.replace(
            '<svg',
            `<svg viewBox="0 0 ${originalWidth} ${originalHeight}"`,
          )
        }
        setQrSvg(svg)
      } catch (e) {
        console.error('Error loading QR code', e)
        toast.danger(message('Failed to generate QR code. Please try again.'))
      } finally {
        setIsLoading(false)
      }
    }
    loadQrCode()
  }, [shortUrl])

  const downloadSVG = () => {
    if (!qrSvg) return
    setDownloadFormat('svg')
    const blob = new Blob([qrSvg], {type: 'image/svg+xml'})
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `qr-code-${shortUrl.split('/').pop()}.svg`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
    toast.positive(message('QR Code SVG downloaded successfully'))
    setTimeout(() => setDownloadFormat(null), 2000)
  }

  const downloadPNG = () => {
    if (!qrSvg || !qrRef.current) return
    setDownloadFormat('png')
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    const img = new Image()
    const svgBlob = new Blob([qrSvg], {type: 'image/svg+xml;charset=utf-8'})
    const url = URL.createObjectURL(svgBlob)

    img.onload = () => {
      canvas.width = 1024
      canvas.height = 1024
      ctx.fillStyle = 'white'
      ctx.fillRect(0, 0, canvas.width, canvas.height)
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
      canvas.toBlob(blob => {
        if (blob) {
          const pngUrl = URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = pngUrl
          link.download = `qr-code-${shortUrl.split('/').pop()}.png`
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          URL.revokeObjectURL(pngUrl)
          toast.positive(message('QR Code PNG downloaded successfully'))
          setTimeout(() => setDownloadFormat(null), 2000)
        }
      }, 'image/png')
      URL.revokeObjectURL(url)
    }
    img.src = url
  }

  const copyUrl = () => {
    copyToClipboard()
    setCopiedRecently(true)
    toast.positive(message('Link copied to clipboard'))
    setTimeout(() => setCopiedRecently(false), 2000)
  }

  return (
    <Dialog className="max-w-lg mx-4" radius="rounded-3xl">
      {/* رأس النافذة - تصميم نظيف ومركز */}
      <DialogHeader className="relative border-b border-gray-100 dark:border-gray-800/50 pb-6 pt-8">
        <div className="text-center">
          {/* أيقونة مع تأثير بصري جذاب */}
          <div className="mx-auto w-16 h-16 bg-gradient-to-br from-indigo-500 via-purple-600 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg shadow-indigo-500/25 mb-4">
            <QrCodeIcon className="w-8 h-8 text-white" />
          </div>
          
          {/* العنوان والوصف */}
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            <Trans message="QR Code Generator" />
            </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            <Trans message="Share your link instantly with anyone" />
            </p>
        </div>
      </DialogHeader>

      <DialogBody className="py-8 px-8">
        <div className="space-y-6">
          
          {/* عرض QR Code */}
          <div className="flex justify-center">
            <div className="relative">
              {/* حاوية QR Code مع تصميم حديث */}
              <div
                ref={qrRef}
                className="bg-white dark:bg-gray-900 rounded-3xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden relative backdrop-blur-sm"
                style={{width: '320px', height: '320px', padding: '24px'}}
              >
                {/* تأثيرات بصرية في الزوايا */}
                <div className="absolute top-0 left-0 w-full h-full">
                  <div className="absolute top-4 left-4 w-6 h-6">
                    <div className="absolute top-0 left-0 w-4 h-1 bg-gradient-to-r from-indigo-500 to-transparent rounded-full" />
                    <div className="absolute top-0 left-0 w-1 h-4 bg-gradient-to-b from-indigo-500 to-transparent rounded-full" />
                  </div>
                  <div className="absolute top-4 right-4 w-6 h-6">
                    <div className="absolute top-0 right-0 w-4 h-1 bg-gradient-to-l from-purple-500 to-transparent rounded-full" />
                    <div className="absolute top-0 right-0 w-1 h-4 bg-gradient-to-b from-purple-500 to-transparent rounded-full" />
                  </div>
                  <div className="absolute bottom-4 left-4 w-6 h-6">
                    <div className="absolute bottom-0 left-0 w-4 h-1 bg-gradient-to-r from-blue-500 to-transparent rounded-full" />
                    <div className="absolute bottom-0 left-0 w-1 h-4 bg-gradient-to-t from-blue-500 to-transparent rounded-full" />
                  </div>
                  <div className="absolute bottom-4 right-4 w-6 h-6">
                    <div className="absolute bottom-0 right-0 w-4 h-1 bg-gradient-to-l from-cyan-500 to-transparent rounded-full" />
                    <div className="absolute bottom-0 right-0 w-1 h-4 bg-gradient-to-t from-cyan-500 to-transparent rounded-full" />
                  </div>
                </div>

                {/* محتوى QR */}
                {isLoading ? (
                  <div className="w-full h-full flex flex-col items-center justify-center space-y-4">
                    <div className="relative">
                      <div className="w-12 h-12 rounded-full border-4 border-gray-200 dark:border-gray-700" />
                      <div className="absolute inset-0 w-12 h-12 rounded-full border-4 border-indigo-500 border-t-transparent animate-spin" />
                    </div>
                    <div className="text-center">
                      <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                        <Trans message="Generating QR Code" />
                      </p>
                      <p className="text-xs text-gray-500">
                        <Trans message="Please wait..." />
                      </p>
                    </div>
                  </div>
                ) : qrSvg ? (
                  <div
                    dangerouslySetInnerHTML={{__html: qrSvg}}
                    className="w-full h-full opacity-90 hover:opacity-100 transition-opacity duration-200"
                  />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center space-y-4">
                    <div className="bg-red-50 dark:bg-red-900/20 rounded-2xl p-4">
                      <QrCodeIcon className="w-10 h-10 text-red-500" />
                    </div>
                    <div className="text-center">
                      <p className="text-sm font-semibold text-red-600 dark:text-red-400 mb-1">
                        <Trans message="Generation Failed" />
                      </p>
                      <p className="text-xs text-gray-500">
                        <Trans message="Please try again" />
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* رابط مباشر */}
          <div className="bg-gradient-to-br from-gray-50 to-gray-100/50 dark:from-gray-800/30 dark:to-gray-800/10 rounded-2xl p-5 border border-gray-200/50 dark:border-gray-700/50">
            <div className="flex items-center gap-2 mb-3">
              <LinkIcon className="w-4 h-4 text-indigo-500" />
              <span className="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                <Trans message="Direct QR Link" />
              </span>
            </div>
            
            <div className="flex items-center gap-3 p-3 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
              <span className="flex-1 text-sm font-mono text-gray-700 dark:text-gray-300 truncate">
                {qrPageUrl}
              </span>
              <Button
                size="sm"
                variant={copiedRecently ? "flat" : "outline"}
                color={copiedRecently ? "positive" : "primary"}
                radius="rounded-lg"
                onClick={copyUrl}
                className={`flex-shrink-0 px-4 py-2 min-h-[36px] font-semibold transition-all duration-200 ${
                  copiedRecently
                    ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/25 scale-105' 
                    : 'hover:scale-105 hover:shadow-md'
                }`}
              >
                {copiedRecently ? (
                  <>
                    <CheckCircleIcon className="w-10 h-10 mr-2" />
                    <span>Copied!</span>
                  </>
                ) : (
                  <>
                    <ContentCopyIcon className="w-10 h-10 mr-2" />
                    <span>Copy</span>
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* خيارات التصدير */}
          {!isLoading && qrSvg && (
            <div className="space-y-4">
              <div className="text-center">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1">
                  <Trans message="Export Options" />
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  <Trans message="Download in your preferred format" />
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-8 max-w-xs mx-auto">
                <Button
                  variant="outline"
                  color="primary"
                  size="lg"
                  radius="rounded-2xl"
                  onClick={downloadSVG}
                  disabled={downloadFormat === 'svg'}
                  className={`py-4 px-6 h-auto transition-all duration-200 ${
                    downloadFormat === 'svg' 
                      ? 'opacity-50 cursor-not-allowed' 
                      : 'hover:scale-105 hover:shadow-lg hover:border-indigo-400'
                  }`}
                >
                  <div className="font-bold text-base">SVG</div>
                </Button>

                <Button
                  variant="flat"
                  color="primary"
                  size="lg"
                  radius="rounded-2xl"
                  onClick={downloadPNG}
                  disabled={downloadFormat === 'png'}
                  className={`py-4 px-6 h-auto transition-all duration-200 ${
                    downloadFormat === 'png' 
                      ? 'opacity-50 cursor-not-allowed' 
                      : 'hover:scale-105 hover:shadow-lg'
                  }`}
                >
                  <div className="font-bold text-base">PNG</div>
                </Button>
              </div>
              
              {/* ملاحظة حجم PNG */}
              <div className="text-center mt-3">
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  <Trans message="PNG format: 1024×1024px" />
                </p>
              </div>
            </div>
          )}
        </div>
      </DialogBody>
{/* تذييل النافذة */}
<DialogFooter className="border-t border-gray-100 dark:border-gray-800/50 pt-6 pb-8 px-0">
  <div className="flex items-center justify-between w-full px-8 gap-6">
    {/* مؤشر الحالة - اليسار */}
    <div className="flex items-center gap-2">
      <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse shadow-lg shadow-emerald-500/50" />
      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
        <Trans message="Ready to scan" />
      </span>
    </div>
    
    {/* زر الإغلاق - اليمين */}
    <Button
      variant="flat"
      color="primary"
      size="md"
      radius="rounded-2xl"
      onClick={() => close()}
      className="font-semibold py-2 bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg shadow-indigo-500/25 hover:shadow-xl hover:scale-105 transition-all duration-200"
      style={{paddingLeft: '1.375rem', paddingRight: '1.375rem'}}
    >
      <Trans message="Done" />
    </Button>
  </div>
</DialogFooter>
    </Dialog>
  )
}